import { useEffect, useRef, useState } from 'react'
import { useSearchParams, Link } from 'react-router-dom'
import { CheckCircle, XCircle, Loader2 } from '@ensafely/checker-ui'
import { setToken } from '../lib/authToken'

type Status = 'loading' | 'success' | 'error'

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const [status, setStatus] = useState<Status>('loading')
  const [message, setMessage] = useState('')

  // Guard against React StrictMode double-invoke and browser re-mounts
  // (back-button, refresh). The ref persists across the StrictMode dev
  // double-invoke, so the POST fires at most once per page load.
  const fired = useRef(false)

  useEffect(() => {
    if (fired.current) return
    fired.current = true

    if (!token) {
      setStatus('error')
      setMessage('No verification token found in URL.')
      return
    }

    fetch('/api/auth/verify-email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then(async (res) => {
        const body = await res.json().catch(() => ({}))
        if (res.ok) {
          setStatus('success')
          setMessage(body.message ?? 'Your email has been verified!')

          // Store the fresh JWT (with email_verified=true) so the
          // verification banner disappears without requiring re-login.
          if (body.token) {
            setToken(body.token)
          }
        } else {
          setStatus('error')
          setMessage(body.error ?? 'Verification failed.')
        }
      })
      .catch(() => {
        setStatus('error')
        setMessage('Network error. Please try again.')
      })
  }, [token])

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md bg-card rounded-lg shadow p-8 text-center">
        <h1 className="text-2xl font-bold mb-6">Email Verification</h1>

        {status === 'loading' && (
          <div className="flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p>Verifying your email address…</p>
          </div>
        )}

        {status === 'success' && (
          <div className="flex flex-col items-center gap-3 text-green-600 dark:text-green-400">
            <CheckCircle className="h-12 w-12" />
            <p className="text-base font-medium">{message}</p>
            <p className="text-sm text-muted-foreground mt-2">
              You can now return to the dashboard.
            </p>
            <Link
              to="/"
              className="mt-4 inline-block px-6 py-2 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
            >
              Go to Dashboard
            </Link>
          </div>
        )}

        {status === 'error' && (
          <div className="flex flex-col items-center gap-3 text-destructive">
            <XCircle className="h-12 w-12" />
            <p className="text-base font-medium">{message}</p>
            <p className="text-sm text-muted-foreground mt-2">
              You can request a new verification link from the app.
            </p>
            <Link
              to="/"
              className="mt-4 inline-block px-6 py-2 rounded bg-primary text-primary-foreground text-sm font-medium hover:opacity-90"
            >
              Back to App
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
